import { useRef, useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from "react-router-dom";
import { API_BASE } from '../utils/config';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from './CustomAlert/AlertContext';
import logo from "../assets/logo.png";
import "./navbar.css";
import "@theme-toggles/react/css/Classic.css";
import { Classic } from "@theme-toggles/react";

const Navbar = ({
  isLoggedIn = false,
  userName = '',
  onSchedule,
  showSchedule = true,
  showCalendar = true,
  showProfile = true,
  showLogout = true,
  onHome,
  onTeam,
  activeTab,
  onTabChange,
}) => {
  const { showConfirm } = useAlert();
  const [notifications, setNotifications] = useState([]);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isDark, toggleTheme } = useTheme();
  const titleRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.user_type || user.role || '';

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';

  const isSuperadminPage = currentPath.startsWith('/superadmin');

  const goHome = () => {
    if (isSuperadminPage) {
      navigate('/superadmin');
    } else if (onHome) {
      onHome();
    } else {
      navigate(isLoggedIn ? '/interview' : '/');
    }
  };

  const fetchNotifications = async (force = false) => {
    // Skip background polling if tab is hidden in background
    if (!force && typeof document !== 'undefined' && document.hidden) return;

    try {
      const response = await fetch(`${API_BASE}/api/notifications`, {
        headers: {
          'x-user-id': user.id || '',
          'x-user-role': userRole || '',
          'X-Tenant-ID': tenant,
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    if (isLoggedIn && user.id) {
      fetchNotifications(true);
      // Polling interval set to 6 minutes (360,000 ms)
      const SIX_MINUTES_MS = 6 * 60 * 1000;
      const interval = setInterval(() => fetchNotifications(false), SIX_MINUTES_MS);

      const handleVisibilityChange = () => {
        if (!document.hidden) fetchNotifications(true);
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [isLoggedIn, user.id]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowNotificationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_BASE}/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          'x-user-id': user.id || '',
          'X-Tenant-ID': tenant,
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      }
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'x-user-id': user.id || '',
          'X-Tenant-ID': tenant,
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      }
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await fetch(`${API_BASE}/api/notifications/${notif.id}/read`, {
          method: 'PUT',
          headers: {
            'x-user-id': user.id || '',
            'X-Tenant-ID': tenant,
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
      } catch (err) {
        console.error('Failed to mark read:', err);
      }
    }

    setShowNotificationDropdown(false);
    
    // Redirect based on type
    if (notif.type === 'REQUISITION') {
      if (userRole === 'BU_HEAD') {
        if (onTabChange) onTabChange('requisitions');
        else navigate('/bu-dashboard/requisitions');
      } else if (userRole === 'MANAGER') {
        if (onTabChange) onTabChange('requisitions');
        else navigate('/manager-dashboard/hiring');
      } else if (userRole === 'HR') {
        navigate('/hr-dashboard');
      }
    } else if (notif.type === 'EXIT') {
      if (userRole === 'BU_HEAD') {
        if (onTabChange) onTabChange('exits');
        else navigate('/bu-dashboard/exits');
      } else if (userRole === 'MANAGER') {
        if (onTabChange) onTabChange('exits');
        else navigate('/manager-dashboard/exits');
      } else if (userRole === 'HR') {
        navigate('/hr-dashboard');
      }
    } else if (notif.type === 'INTERVIEW') {
      if (userRole === 'INTERVIEWER') {
        navigate('/interviewer-dashboard');
      } else if (userRole === 'HR') {
        navigate('/hr-dashboard');
      }
    }
  };

  const handleLogout = async () => {
    const confirmed = await showConfirm('Are you sure you want to logout?');
    if (!confirmed) return;

    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (e) {
      // Ignore network errors during logout cleanup.
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('scheduledInterviews');
      navigate('/login');
    }
  };

  const handleMouseMove = (e) => {
    if (titleRef.current && isDark) {
      const rect = titleRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      titleRef.current.style.setProperty('--mouse-x', `${x}%`);
      titleRef.current.style.setProperty('--mouse-y', `${y}%`);
    }
  };

  const handleMouseLeave = () => {
    if (titleRef.current && isDark) {
      titleRef.current.style.setProperty('--mouse-x', '50%');
      titleRef.current.style.setProperty('--mouse-y', '50%');
    }
  };

  return (
    <>
      {isLoggedIn && <div className="navbar-spacer" aria-hidden="true" />}
      <div className={`navbar ${isLoggedIn ? 'navbar-post-login navbar-fixed' : ''}`}>
        <div
          className="nav-left"
          onClick={goHome}
          role="button"
          tabIndex={0}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goHome();
            }
          }}
          aria-label="Go to home page"
        >
          <img src={logo} alt="logo" className="header-title-logo" />
          <span className="title" ref={titleRef}>Smart Interview</span>
        </div>

        <div className="nav-right">
          {/* Theme Toggle (Superadmin Only) */}
          {isSuperadminPage && (
            <button
              onClick={toggleTheme}
              className={`theme-toggle-slider ${isDark ? 'is-dark' : 'is-light'}`}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label="Toggle dark mode"
            >
              <span className="theme-toggle-thumb" aria-hidden="true">
                <Classic 
                  toggled={isDark} 
                  duration={750}
                  className="thumb-classic-icon"
                />
              </span>
            </button>
          )}


          {!isLoggedIn && !isSuperadminPage && (
            <div className="nav-links-public">
              <Link
                to="/features"
                className={`nav-tab ${currentPath === '/features' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
              >
                Features
              </Link>
              <Link
                to="/how"
                className={`nav-tab ${currentPath === '/how' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
              >
                How it works
              </Link>
              <Link
                to="/contact"
                className={`nav-tab ${currentPath === '/contact' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
              >
                Contact
              </Link>
              <Link
                to="/privacy"
                className={`nav-tab ${currentPath === '/privacy' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
              >
                Privacy
              </Link>
              <Link
                to="/signup"
                className={`nav-tab ${currentPath === '/signup' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
              >
                Register
              </Link>
              <Link
                to="/login"
                className={`nav-tab ${currentPath === '/login' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
              >
                Login
              </Link>
            </div>
          )}

          {isLoggedIn && !isSuperadminPage && userRole !== 'HR' && (
            <div className={`nav-links-auth ${userRole === 'BU_HEAD' ? 'bu-nav-tabs' : ''}`}>
              {userRole !== 'MANAGER' && userRole !== 'BU_HEAD' && (
                <button
                  className={`nav-tab ${(activeTab === 'dashboard' || currentPath === '/interview') && activeTab !== 'hiring-pipeline' && activeTab !== 'manager-dashboard' && activeTab !== 'bu-dashboard' && activeTab !== 'interviewer-dashboard' && activeTab !== 'schedule' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                  onClick={onHome || (() => navigate('/interview'))}
                >
                  Rooms
                </button>
              )}

              {userRole === 'HR' && (
                <button
                  className={`nav-tab ${currentPath === '/hr-dashboard' || activeTab === 'hiring-pipeline' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                  onClick={() => navigate('/hr-dashboard')}
                >
                  Hiring Pipeline
                </button>
              )}

              {userRole === 'MANAGER' && (
                <>
                  <button
                    className={`nav-tab ${activeTab === 'overview' || currentPath === '/manager-dashboard' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('overview') : navigate('/manager-dashboard')}
                  >
                    Overview
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'roles' || currentPath.endsWith('/roles') ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('roles') : navigate('/manager-dashboard/roles')}
                  >
                    Team Roles
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'requisitions' || currentPath.endsWith('/hiring') ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('requisitions') : navigate('/manager-dashboard/hiring')}
                  >
                    Hiring
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'exits' || currentPath.endsWith('/exits') ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('exits') : navigate('/manager-dashboard/exits')}
                  >
                    Exits
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'onboard' || currentPath.endsWith('/onboard') ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('onboard') : navigate('/manager-dashboard/onboard')}
                  >
                    Onboard Team
                  </button>
                </>
              )}



               {userRole === 'BU_HEAD' && (
                <>
                  <button
                    className={`nav-tab ${activeTab === 'dashboard' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('dashboard') : navigate('/bu-dashboard', { state: { activeTab: 'dashboard' } })}
                  >
                    Dashboard
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'employees' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('employees') : navigate('/bu-dashboard', { state: { activeTab: 'employees' } })}
                  >
                    Staff
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'leavers' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('leavers') : navigate('/bu-dashboard', { state: { activeTab: 'leavers' } })}
                  >
                    Exits
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'requisitions' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('requisitions') : navigate('/bu-dashboard', { state: { activeTab: 'requisitions' } })}
                  >
                    Hiring
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'interviews' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('interviews') : navigate('/bu-dashboard', { state: { activeTab: 'interviews' } })}
                  >
                    Interviews
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'positions' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('positions') : navigate('/bu-dashboard', { state: { activeTab: 'positions' } })}
                  >
                    Placements
                  </button>
                  <button
                    className={`nav-tab ${activeTab === 'onboard' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                    onClick={() => onTabChange ? onTabChange('onboard') : navigate('/bu-dashboard', { state: { activeTab: 'onboard' } })}
                  >
                    Import
                  </button>
                </>
              )}

              {userRole === 'INTERVIEWER' && (
                <button
                  className={`nav-tab ${currentPath === '/interviewer-dashboard' || activeTab === 'interviewer-dashboard' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                  onClick={() => navigate('/interviewer-dashboard')}
                >
                  Interviewer Dashboard
                </button>
              )}

              {showSchedule && userRole !== 'MANAGER' && userRole !== 'BU_HEAD' && (
                <button
                  className={`nav-tab ${activeTab === 'schedule' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                  onClick={onSchedule || (() => navigate('/interview', { state: { step: 'calendar' } }))}
                >
                  + Schedule
                </button>
              )}

              {showCalendar && userRole !== 'MANAGER' && userRole !== 'BU_HEAD' && (
                <button
                  className={`nav-tab ${currentPath === '/calendar-view' ? 'nav-tab-primary' : 'nav-tab-secondary'}`}
                  onClick={() => navigate('/calendar-view')}
                >
                  Calendar
                </button>
              )}
            </div>
          )}



          {isLoggedIn && !isSuperadminPage && (
            <div className="nav-notifications-container" ref={dropdownRef} style={{ position: 'relative', marginRight: '16px' }}>
              <button 
                className="nav-notifications-bell-btn" 
                onClick={() => {
                  const nextState = !showNotificationDropdown;
                  setShowNotificationDropdown(nextState);
                  if (nextState) fetchNotifications(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  position: 'relative',
                  cursor: 'pointer',
                  color: 'var(--text-main, #334155)',
                  padding: '8px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <span 
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      borderRadius: '50%',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {notifications.filter(n => !n.is_read).length}
                  </span>
                )}
              </button>

              {showNotificationDropdown && (
                <div 
                  className="nav-notifications-dropdown"
                  style={{
                    position: 'absolute',
                    top: '45px',
                    right: '0',
                    width: '320px',
                    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.98))',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 1000,
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <div 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border-color, #e2e8f0)',
                      backgroundColor: 'rgba(248, 250, 252, 0.5)'
                    }}
                  >
                    <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-main, #0f172a)' }}>Notifications</span>
                    {notifications.filter(n => !n.is_read).length > 0 && (
                      <button 
                        onClick={handleMarkAllRead} 
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#4f46e5',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          padding: '0'
                        }}
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        You're all caught up! 🔔
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div 
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid var(--border-color, #f1f5f9)',
                            cursor: 'pointer',
                            backgroundColor: notif.is_read ? 'transparent' : 'rgba(79, 70, 229, 0.04)',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'flex-start',
                            textAlign: 'left'
                          }}
                        >
                          <div style={{ fontSize: '16px', marginTop: '2px' }}>
                            {notif.type === 'REQUISITION' ? '💼' : notif.type === 'EXIT' ? '🚪' : notif.type === 'INTERVIEW' ? '🗓️' : '🔔'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                              <span style={{ fontWeight: notif.is_read ? '500' : '700', fontSize: '13px', color: 'var(--text-main, #1e293b)' }}>
                                {notif.title}
                              </span>
                              {!notif.is_read && (
                                <span 
                                  onClick={(e) => handleMarkRead(notif.id, e)}
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: '#4f46e5',
                                    borderRadius: '50%',
                                    display: 'inline-block'
                                  }}
                                  title="Mark as read"
                                />
                              )}
                            </div>
                            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted, #64748b)', lineHeight: '1.4' }}>
                              {notif.message}
                            </p>
                            <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginTop: '4px' }}>
                              {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {isLoggedIn && showProfile && !isSuperadminPage && (
            <div className="nav-profile-avatar-container">
              <div className="nav-profile-avatar" title="View Profile">
                {userName
                  ? userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                  : 'HR'}
              </div>
              <div className="nav-profile-dropdown" aria-hidden="true">
                <div className="nav-profile-dropdown-label">Logged in as</div>
                <div className="nav-profile-dropdown-name">{userName || 'HR'}</div>
                <div className="nav-role-badge">{userRole === 'HR' ? 'HR' : userRole}</div>
              </div>
            </div>
          )}

          {isLoggedIn && showLogout && !isSuperadminPage && (
            <button onClick={handleLogout} className="nav-btn-logout" aria-label="Logout" title="Logout">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;